import { Box, Typography } from '@mui/material';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useErrorHandlerContextState } from 'contexts/error-handler/ErrorHandlerContext';
import { useGet } from 'hooks/use-get';
import { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import _ from 'underscore';
import { isNullOrUndefined } from 'utils/empty-or-null';
import { getCalibrations } from 'views/bluviu-nutrition/features/calibrations/api/calibration';
import { CalibrationState } from 'views/bluviu-nutrition/features/calibrations/types/calibration-state.enum';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';
import { UpdateScenarioRequest } from 'views/bluviu-nutrition/features/scenarios/types/update-scenario-input.type';
import isScenarioDisabled from 'views/bluviu-nutrition/features/scenarios/utils/validate-scenario';

type Props = {
  control: Control<UpdateScenarioRequest, any>;
  isLoading: boolean;
};
const PAGE_NAME_ID = 'bluViuNutrition.setUp';

const ScenarioInformation: React.FC<Props> = ({ control, isLoading }) => {
  const intl = useIntl();
  const params = useParams<ScenariosParams>();
  const {
    scenarioQuery: { data: scenario, isLoading: isScenarioLoading },
  } = useScenarioState();
  const { errors } = useErrorHandlerContextState();

  const { data: calibrations, isLoading: isCalibrationsLoading } = useGet(
    ['getCalibrations', params.globalSiteId],
    ({ queryKey }) => getCalibrations(String(queryKey[1])),
    {
      select: (data) =>
        _.chain(data)
          .filter((calibration) => calibration.state === CalibrationState.COMPLETED || calibration.state === CalibrationState.MODIFIED)
          .sortBy('name')
          .value(),
    }
  );

  if (isScenarioLoading) return <LoadingSpinner />;
  if (!scenario) return;

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Box display={'flex'} flexDirection={'row'} gap={4}>
        <TextField
          dark
          fullWidth
          placeholder={intl.formatMessage({ id: `${PAGE_NAME_ID}.label.name.placeholder` })}
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.label.name.title` })}
          disabled={isScenarioDisabled(scenario, isLoading, true)}
          error={!!errors.find((e) => e.field?.includes('name'))}
          helperText={errors.find((e) => e.field?.includes('name'))?.message}
          controller={{
            name: 'name',
            control: control,
            rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
          }}
        />
        <TextField
          dark
          fullWidth
          value={`Effective energy Gt2`}
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.label.name.modelName` })}
          disabled={true}
        />
        <Select
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.label.calibration` })}
          dark
          fullWidth
          isLoading={isCalibrationsLoading}
          disabled={isScenarioDisabled(scenario, isLoading, true)}
          controller={{
            control,
            name: 'calibrationId',
            rules: {
              required: intl.formatMessage({ id: 'form.rules.required' }),
              validate: {
                hasNoValue: (value: string) => !isNullOrUndefined(value),
              },
            },
          }}
          noResults={{
            text: intl.formatMessage({ id: 'general.noResults' }),
            show: !calibrations?.length,
          }}>
          {calibrations?.map((calibration, idx) => (
            <RadioSelectItem
              key={idx}
              value={calibration.id}
              sx={{ flexDirection: 'column', alignItems: 'flex-start', height: 'auto' }}>
              <Typography>{calibration.name} </Typography>
              <small>
                WG: {calibration.growthCoefficient} / FI: {calibration.feedIntakeCoefficient}
              </small>
            </RadioSelectItem>
          ))}
        </Select>
      </Box>
      <Box display={'flex'} flexDirection={'row'} gap={4}>
        <TextField
          dark
          fullWidth
          placeholder={intl.formatMessage({ id: `${PAGE_NAME_ID}.label.description.placeholder` })}
          multiline
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.label.description.title` })}
          disabled={isScenarioDisabled(scenario, isLoading, true)}
          controller={{
            name: 'description',
            control: control,
          }}
        />
      </Box>
    </Box>
  );
};

export default ScenarioInformation;
